import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Button, cn, Dialog } from '@erp/ui';
import { api } from '../lib/api';
import { tempoRelativo } from '../lib/crm';

interface Notificacao {
  id: string;
  tipo: string;
  titulo: string;
  texto: string | null;
  link: string | null;
  lida: boolean;
  criadoEm: string;
}

/** Sino do cabeçalho: lead novo, aprovação de desconto, carro desejado que chegou etc. */
export function Notificacoes() {
  const [aberto, setAberto] = useState(false);
  const navegar = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['notificacoes'],
    queryFn: () => api<{ naoLidas: number; itens: Notificacao[] }>('/notificacoes'),
    refetchInterval: 30_000,
  });
  const ler = useMutation({
    mutationFn: (ids?: string[]) => api('/notificacoes/lidas', { method: 'POST', json: { ids } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notificacoes'] }),
  });
  const n = q.data?.naoLidas ?? 0;
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={n ? `Notificações (${n} novas)` : 'Notificações'}
        onClick={() => setAberto(true)}
      >
        <Bell />
        {n > 0 && (
          <span
            className="absolute right-1 top-1 min-w-4 rounded-full bg-destructive px-1 text-[10px] leading-4 text-white"
            aria-hidden
          >
            {n > 9 ? '9+' : n}
          </span>
        )}
      </Button>
      <Dialog aberto={aberto} aoFechar={() => setAberto(false)} titulo="Notificações">
        <div className="flex flex-col gap-2">
          {n > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="self-end"
              onClick={() => ler.mutate(undefined)}
            >
              Marcar todas como lidas
            </Button>
          )}
          {!q.data?.itens.length && <p className="text-sm text-muted-foreground">Nada por aqui.</p>}
          <ul className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto">
            {q.data?.itens.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  className={cn(
                    'w-full rounded-lg p-2 text-left text-sm hover:bg-accent',
                    !i.lida && 'bg-primary/5',
                  )}
                  onClick={() => {
                    if (!i.lida) ler.mutate([i.id]);
                    setAberto(false);
                    if (i.link) navegar(i.link);
                  }}
                >
                  <span className={cn('block', !i.lida && 'font-medium')}>{i.titulo}</span>
                  {i.texto && <span className="block text-muted-foreground">{i.texto}</span>}
                  <span className="text-xs text-muted-foreground">{tempoRelativo(i.criadoEm)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </>
  );
}
